
import React, { useEffect, useRef } from "react";
import { format } from "date-fns";
import { Habit } from "@/context/SupabaseHabitContext";
import { X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface HabitInfoPopupProps {
  date: Date;
  habits: Habit[];
  completedHabits: Habit[];
  position: { x: number; y: number };
  onClose: () => void;
}

export default function HabitInfoPopup({
  date,
  habits,
  completedHabits,
  position,
  onClose
}: HabitInfoPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  
  // Format date for display
  const dateFormatted = format(date, 'MMMM d, yyyy');
  const completedCount = completedHabits.length;
  const totalHabits = habits.length;
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    // Add event listener on mount
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  // Calculate optimal position to ensure popup stays in viewport
  const calcPosition = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Default width of the popup card
    const popupWidth = 320;
    const popupHeight = 200;
    
    let left = position.x;
    let top = position.y;
    
    // Ensure the popup doesn't go off the right edge
    if (left + popupWidth > screenWidth) {
      left = screenWidth - popupWidth - 10;
    }
    
    // Ensure the popup doesn't go off the bottom
    if (top + popupHeight > screenHeight) {
      top = screenHeight - popupHeight - 10;
    }
    
    // Make sure it never goes off the left or top
    left = Math.max(10, left);
    top = Math.max(10, top);
    
    return { left, top };
  };
  
  const { left, top } = calcPosition();
  
  return (
    <>
      {/* Semi-transparent backdrop for mobile */}
      <div 
        className="fixed inset-0 bg-black/20 z-40 md:hidden"
        onClick={onClose}
      />
      
      <div 
        ref={popupRef}
        className="fixed z-50 shadow-lg animate-in zoom-in-90 duration-200"
        style={{ 
          left: `${left}px`, 
          top: `${top}px`,
          maxWidth: "320px"
        }}
      >
        <Card>
          <CardHeader className="py-3 px-4 flex flex-row justify-between items-center">
            <CardTitle className="text-base font-medium">{dateFormatted}</CardTitle>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-accent"
            >
              <X size={16} />
            </button>
          </CardHeader>
          <CardContent className="py-3 px-4">
            {completedCount === 0 ? (
              <p className="text-sm text-muted-foreground">No habits completed on this day</p>
            ) : (
              <>
                <p className="text-sm mb-2">
                  <span className="font-medium">{completedCount}</span> of{" "}
                  <span className="font-medium">{totalHabits}</span> habits completed
                </p>
                <div className="space-y-1">
                  {completedHabits.map(habit => (
                    <div 
                      key={habit.id} 
                      className="flex items-center gap-2"
                    >
                      <div 
                        className="w-3 h-3 rounded-sm" 
                        style={{ backgroundColor: habit.color.filled }}
                      />
                      <span className="text-sm">{habit.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
